import { ApiClient, ApiResponse } from '../client';
import { Product, ListOptions } from '../models';

export interface ProductLookupResult {
  id: string;
  slug?: string;
  name: string;
  variantSku?: string | null;
}

/**
 * Servicio para gestionar productos
 */
export class ProductService {
  private client: ApiClient;

  constructor(client: ApiClient) {
    this.client = client;
  }

  /**
   * Obtener listado de marcas únicas
   */
  public async getBrands(): Promise<ApiResponse<string[]>> {
    return await this.client.get<string[]>('/products/brands', {
      useCache: true
    });
  }

  /**
   * Obtener listado de productos con filtros y paginación
   * @param options Opciones de filtrado y paginación
   */
  public async getProducts(options: ListOptions = {}): Promise<ApiResponse<Product[]>> {
    const { page, limit, sort, order, search, filter } = options;

    // Construir parámetros de query
    const params: Record<string, any> = {
      page,
      limit,
      sort,
      order
    };

    // Añadir parámetro de búsqueda si existe
    if (search) {
      params.search = search;
    }

    // Añadir filtros específicos
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        params[key] = value;
      });
    }

    return await this.client.get<Product[]>('/products', {
      params,
      useCache: true
    });
  }

  /**
   * Obtener un producto por su ID
   * @param id ID del producto
   */
  public async getProduct(id: string): Promise<ApiResponse<Product>> {
    return await this.client.get<Product>(`/products/${id}`, {
      useCache: true
    });
  }

  /**
   * Buscar productos por texto
   * @param query Texto a buscar
   */
  public async searchProducts(query: string): Promise<ApiResponse<Product[]>> {
    return await this.client.get<Product[]>('/products/search', {
      params: { q: query },
      useCache: true,
      cacheTTL: 5 * 60 * 1000 // 5 minutos
    });
  }

  /** Lookup rápido por referencia exacta (SKU o EAN) */
  /** Obtener sugerencias rápidas del buscador */
  public async lookupByRef(ref: string): Promise<ApiResponse<ProductLookupResult | null>> {
    return await this.client.get<ProductLookupResult | null>('/products/lookup', {
      params: { ref },
      useCache: false
    });
  }

  public async suggest(query: string): Promise<ApiResponse<Array<{ id: string; title: string; slug: string }>>> {
    return await this.client.get('/search/suggest', { params: { q: query }, useCache: true, cacheTTL: 60 * 1000 });
  }

  /** Búsqueda con reranking semántico */
  public async searchReranked(query: string, limit: number = 20): Promise<ApiResponse<Product[]>> {
    return await this.client.get<Product[]>('/search/reranked', { params: { q: query, limit } });
  }

  /**
   * Obtener productos destacados
   */
  public async getFeaturedProducts(): Promise<ApiResponse<Product[]>> {
    return await this.client.get<Product[]>('/products', {
      params: { featured: true },
      useCache: true,
      cacheTTL: 15 * 60 * 1000 // 15 minutos
    });
  }

  /**
   * Obtener productos por categoría
   * @param categoryId ID de la categoría
   */
  public async getProductsByCategory(categoryId: string): Promise<ApiResponse<Product[]>> {
    return await this.client.get<Product[]>('/products', {
      params: { category: categoryId },
      useCache: true
    });
  }

  /**
   * Crear un nuevo producto (admin)
   * @param productData Datos del producto a crear
   */
  public async createProduct(productData: Partial<Product>): Promise<ApiResponse<Product>> {
    return await this.client.post<Product>('/products', productData);
  }

  /**
   * Actualizar un producto existente (admin)
   * @param id ID del producto
   * @param productData Datos actualizados del producto
   */
  public async updateProduct(id: string, productData: Partial<Product>): Promise<ApiResponse<Product>> {
    return await this.client.put<Product>(`/products/${id}`, productData);
  }

  /**
   * Eliminar un producto (admin)
   * @param id ID del producto
   */
  public async deleteProduct(id: string): Promise<ApiResponse<null>> {
    return await this.client.delete<null>(`/products/${id}`);
  }

  /**
   * Subir una imagen para un producto (admin)
   * @param productId ID del producto
   * @param imageFile Archivo de imagen
   */
  public async uploadProductImage(productId: string, imageFile: File): Promise<ApiResponse<Product>> {
    // Crear un FormData para enviar el archivo
    const formData = new FormData();
    formData.append('file', imageFile);

    // No pasar headers vacíos para que el cliente incluya Authorization automáticamente
    return await this.client.post<Product>(`/products/${productId}/image`, formData);
  }

  /**
   * Subir una imagen para una variante de producto (admin)
   * @param productId ID del producto
   * @param variantIndex Índice de la variante
   * @param imageFile Archivo de imagen
   */
  public async uploadVariantImage(productId: string, variantIndex: number, imageFile: File): Promise<ApiResponse<Product>> {
    const formData = new FormData();
    formData.append('file', imageFile);

    // No pasar headers vacíos para que el cliente incluya Authorization automáticamente
    return await this.client.post<Product>(`/products/${productId}/variants/${variantIndex}/image`, formData);
  }

  /**
   * Obtener productos similares o relacionados
   * @param productId ID del producto del que buscar similares
   * @param limit Número máximo de productos a devolver
   */
  public async getSimilarProducts(productId: string, limit: number = 4): Promise<ApiResponse<Product[]>> {
    return await this.client.get<Product[]>(`/products/${productId}/similar`, {
      params: { limit },
      useCache: true
    });
  }

  /**
   * Obtener estadísticas de productos (admin/beeadmin)
   */
  public async getProductStats(): Promise<ApiResponse<any>> {
    return await this.client.get<any>('/products/statistics');
  }
} 
